import React, { useState, useEffect } from 'react';
import { Button, Typography, Container, Grid, useTheme, Box } from '@mui/material';
import Dropzone, { DropEvent } from 'react-dropzone';
import { getUploadStyles } from './styles'

interface UploadProps {
  fileType: string;
  disabled?: boolean;
  onUpload(file: File): Promise<{ success: boolean; errorMessage?: string }>;
}

export default function Upload({ fileType, disabled, onUpload }: UploadProps) {
  const theme = useTheme();
  const styles = getUploadStyles(theme);
  const fileNamePattern = /[`!@#$%^&*()+=[\]{};':"\\|,<>/?~]/;
  const [selectedFile, setSelectedFile] = useState<File | undefined>();
  const [fileErr, setFileErr] = useState('');
  const [fileSuccess, setFileSuccess] = useState('');
  const [disableSend, setDisableSend] = useState(true);

  useEffect(() => {
    if (!disabled && selectedFile) {
      setDisableSend(false);
    } else {
      setDisableSend(true);
    }
  }, [disabled, selectedFile]);

  const handleFileSelect = (file: File, event: DropEvent) => {
    event.preventDefault();

    if (!file.name.includes(`.${fileType}`)) {
      setErrorsOnLoad(`Invalid file format! ${fileType.toUpperCase()} files only`);
      return;
    }

    if (fileNamePattern.test(file.name)) {
      setErrorsOnLoad('File name must contain only alphanumeric values!');
      return;
    }

    setSelectedFile(file);
    setFileErr('');
    setFileSuccess('');
  };

  const uploadFile = async () => {
    setDisableSend(true);

    const uploadResult = await onUpload(selectedFile!);

      if (uploadResult.success) {
        resetFileDropArea();
        setFileSuccess('Uploaded successfully!');
      } else {
        resetFileDropArea();
        setErrorsOnLoad(uploadResult.errorMessage ?? 'Error on upload');
      }
  };

  const setErrorsOnLoad = (msg: string) => {
    setSelectedFile(undefined);
    setFileErr(msg);
    setFileSuccess('');
    setDisableSend(true);
  }

  const resetFileDropArea = () => {
    setSelectedFile(undefined);
    setFileErr('');
    setFileSuccess('');
    setDisableSend(true);
  }

  return (
    <Grid>
      <Dropzone
        onDrop={(acceptedFiles, _, event) => { handleFileSelect(acceptedFiles[0], event) }}
        multiple={false}
      >
        {({ getRootProps, getInputProps }) => (
          <section>
            <Box sx={styles.fileUpload} {...getRootProps()}>
              <input {...getInputProps()} />
              {selectedFile
                ? <Typography sx={styles.fileUploadText}>Your file: {selectedFile.name}</Typography>
                : <Typography sx={styles.fileUploadText}>
                  Drag and drop a {fileType.toUpperCase()} here, or click to select a file. <br />
                        (Only *.{fileType} files will be accepted)
                      </Typography>
              }
            </Box>
          </section>
        )}
      </Dropzone>
      <Container sx={styles.buttonsContainer}>
        <Button sx={styles.button} variant="contained" onClick={resetFileDropArea}>Clear</Button>
        <Button sx={styles.button} variant="contained" color="primary" disabled={disableSend} onClick={uploadFile}>
          Upload file
        </Button>
      </Container>
      {fileErr ? (
        <Typography component="p" sx={{ ...styles.errMsg, textAlign: 'left', mb: 2 }}>
          Error: {fileErr}
        </Typography>
      ) : undefined}
      {fileSuccess ? (
        <Typography component="p" sx={{ ...styles.successMsg, textAlign: 'left', mb: 2 }}>
          Status: {fileSuccess}
        </Typography>
      ) : undefined}
    </Grid>
  );
}
